/**
 * Reading and writing a custom question's `optionsJson` blob.
 *
 * The backend stores every per-option behaviour the form builder supports in
 * one free-form JSON string on the question, so this module is the single place
 * that knows the shape:
 *
 *   {
 *     "options": ["Cabinets", "Exterior", "Interior"],
 *     "requestOnlyIndexes": [1],
 *     "displayType": "radio",
 *     "autoAssignedUserIds": { "0": "<userId>" },
 *     "excludedUserIds": { "1": ["<userId>"] },
 *     "durationExtensionMinutes": { "2": 30 },
 *     "optionTargets": { "0": "<branchKey>" },
 *     "branchKey": "<uuid>",
 *     "_isDescriptionField": true
 *   }
 *
 * A legacy format also exists where `optionsJson` is a bare string array; the
 * parser accepts it and normalises to the object shape.
 */

import {
  isDurationExtensionPreset,
  normalizeUserIds,
  parseDurationExtensions,
  parseUserExclusions,
} from './questionOptionMaps';

/** The question kinds the builder offers, keyed by their UI identifier. */
export type QuestionUiType = 'text' | 'textarea' | 'select' | 'radio' | 'checkbox' | 'file';

/** UI types whose answers come from a fixed option list. */
export const CHOICE_UI_TYPES: readonly QuestionUiType[] = ['select', 'radio', 'checkbox'];

export function isChoiceUiType(type: string | null | undefined): boolean {
  return CHOICE_UI_TYPES.includes(type as QuestionUiType);
}

/**
 * API field type per UI type. Radio and checkbox share the `Checkbox` field
 * type; they're told apart by `displayType` inside `optionsJson`.
 */
export const UI_TO_FIELD_TYPE: Record<QuestionUiType, string> = {
  text: 'ShortText',
  textarea: 'Paragraph',
  select: 'Dropdown',
  radio: 'Checkbox',
  checkbox: 'Checkbox',
  file: 'Attachments',
};

/**
 * Labels are written for the operator, not the API: someone setting up a
 * booking form knows what "multiple choice" and "photos" mean, but not what a
 * radio button or an attachment field is.
 */
export const FIELD_TYPE_MAPPING: Record<string, { ui: QuestionUiType; label: string }> = {
  ShortText: { ui: 'text', label: 'Short answer' },
  Paragraph: { ui: 'textarea', label: 'Long answer' },
  Dropdown: { ui: 'select', label: 'Dropdown' },
  Checkbox: { ui: 'checkbox', label: 'Pick any' },
  Attachments: { ui: 'file', label: 'Photos' },
};

/** True when the stored question is a radio (Checkbox field + radio displayType). */
export function isRadioQuestion(
  apiType: string | null | undefined,
  optionsJson: string | null | undefined
): boolean {
  if (apiType !== 'Checkbox' || !optionsJson) return false;
  try {
    const parsed = JSON.parse(optionsJson);
    return !!parsed && !Array.isArray(parsed) && parsed.displayType === 'radio';
  } catch {
    return false;
  }
}

/** Map a stored question back to the UI type the editor works in. */
export function resolveUiType(
  apiType: string | null | undefined,
  optionsJson: string | null | undefined
): QuestionUiType {
  if (isRadioQuestion(apiType, optionsJson)) return 'radio';
  return FIELD_TYPE_MAPPING[apiType || '']?.ui || 'text';
}

/** Human-readable type name for a stored question. */
export function resolveDisplayLabel(
  apiType: string | null | undefined,
  optionsJson: string | null | undefined
): string {
  if (isRadioQuestion(apiType, optionsJson)) return 'Pick one';
  return FIELD_TYPE_MAPPING[apiType || '']?.label || apiType || 'Short answer';
}

export interface ParsedFieldOptions {
  options: string[];
  /** Per-option flag: customer submits a request for review instead of booking. */
  requestOnlyFlags: boolean[];
  displayType?: string;
  /** Option index (string key) -> user id the answer pins the booking to. */
  autoAssignedUserIds: Record<string, string>;
  /** Option index (string key) -> user ids smart assignment must skip. */
  excludedUserIds: Record<string, string[]>;
  /** Option index (string key) -> extra minutes added to the visit. */
  durationExtensionMinutes: Record<string, number>;
  /** Option index (string key) -> branchKey (or builder id) the answer reveals. */
  optionTargets: Record<string, string>;
  /** Stable identifier other questions use to target this one. */
  branchKey?: string;
  /** Marks the single free-text "describe your project" field. */
  isDescriptionField: boolean;
}

const EMPTY_PARSED = (): ParsedFieldOptions => ({
  options: [],
  requestOnlyFlags: [],
  autoAssignedUserIds: {},
  excludedUserIds: {},
  durationExtensionMinutes: {},
  optionTargets: {},
  isDescriptionField: false,
});

export function parseFieldOptions(optionsJson: string | null | undefined): ParsedFieldOptions {
  if (!optionsJson) return EMPTY_PARSED();
  try {
    const parsed = JSON.parse(optionsJson);

    if (Array.isArray(parsed)) {
      return {
        ...EMPTY_PARSED(),
        options: parsed as string[],
        requestOnlyFlags: (parsed as string[]).map(() => false),
      };
    }

    if (!parsed || typeof parsed !== 'object') return EMPTY_PARSED();

    const options: string[] = Array.isArray(parsed.options) ? parsed.options : [];
    const requestOnlyFlags = options.map((_, i) =>
      Array.isArray(parsed.requestOnlyIndexes) && parsed.requestOnlyIndexes.includes(i)
    );

    const autoAssignedUserIds: Record<string, string> = {};
    if (parsed.autoAssignedUserIds && typeof parsed.autoAssignedUserIds === 'object') {
      Object.entries(parsed.autoAssignedUserIds as Record<string, unknown>).forEach(([k, v]) => {
        if (typeof v === 'string' && v.length > 0) autoAssignedUserIds[k] = v;
      });
    }

    const optionTargets: Record<string, string> = {};
    if (parsed.optionTargets && typeof parsed.optionTargets === 'object') {
      Object.entries(parsed.optionTargets as Record<string, unknown>).forEach(([k, v]) => {
        if (typeof v === 'string' && v.length > 0) optionTargets[k] = v;
      });
    }

    return {
      options,
      requestOnlyFlags,
      displayType: typeof parsed.displayType === 'string' ? parsed.displayType : undefined,
      autoAssignedUserIds,
      excludedUserIds: parseUserExclusions(optionsJson),
      durationExtensionMinutes: parseDurationExtensions(optionsJson),
      optionTargets,
      branchKey: typeof parsed.branchKey === 'string' ? parsed.branchKey : undefined,
      isDescriptionField: parsed._isDescriptionField === true,
    };
  } catch {
    return EMPTY_PARSED();
  }
}

export interface FieldOptionsInput {
  options: string[];
  requestOnlyFlags?: boolean[];
  displayType?: string;
  autoAssignedUserIds?: Record<string, string>;
  excludedUserIds?: Record<string, string[]>;
  optionTargets?: Record<string, string>;
  branchKey?: string;
  durationExtensionMinutes?: Record<string, number>;
  isDescriptionField?: boolean;
}

/**
 * Serialize back to `optionsJson`, dropping anything that can't apply: indexes
 * outside the current option list, and per-option behaviours on a request-only
 * option (those never reach a slot, so there is nothing to assign or lengthen).
 */
export function stringifyFieldOptions(input: FieldOptionsInput): string {
  const {
    options,
    requestOnlyFlags,
    displayType,
    autoAssignedUserIds,
    excludedUserIds,
    optionTargets,
    branchKey,
    durationExtensionMinutes,
    isDescriptionField,
  } = input;

  const inRange = (key: string): number | null => {
    const idx = Number(key);
    if (!Number.isFinite(idx) || idx < 0 || idx >= options.length) return null;
    return idx;
  };

  const obj: Record<string, unknown> = {
    options,
    requestOnlyIndexes: (requestOnlyFlags || [])
      .map((flag, i) => (flag ? i : -1))
      .filter(i => i >= 0),
  };

  if (displayType === 'radio') obj.displayType = 'radio';
  if (branchKey) obj.branchKey = branchKey;
  if (isDescriptionField) obj._isDescriptionField = true;

  const cleanedTargets: Record<string, string> = {};
  Object.entries(optionTargets || {}).forEach(([k, v]) => {
    const idx = inRange(k);
    if (idx === null || !v) return;
    cleanedTargets[String(idx)] = v;
  });
  if (Object.keys(cleanedTargets).length > 0) obj.optionTargets = cleanedTargets;

  const cleanedAssigned: Record<string, string> = {};
  Object.entries(autoAssignedUserIds || {}).forEach(([k, v]) => {
    const idx = inRange(k);
    if (idx === null || !v || requestOnlyFlags?.[idx]) return;
    cleanedAssigned[String(idx)] = v;
  });
  if (Object.keys(cleanedAssigned).length > 0) obj.autoAssignedUserIds = cleanedAssigned;

  // An answer that pins one person has already decided who takes the booking,
  // so exclusions on it can only ever contradict the pin — drop them with it.
  const cleanedExcluded: Record<string, string[]> = {};
  Object.entries(excludedUserIds || {}).forEach(([k, v]) => {
    const idx = inRange(k);
    if (idx === null || !Array.isArray(v) || requestOnlyFlags?.[idx]) return;
    if (cleanedAssigned[String(idx)]) return;
    const userIds = normalizeUserIds(v);
    if (userIds.length > 0) cleanedExcluded[String(idx)] = userIds;
  });
  if (Object.keys(cleanedExcluded).length > 0) obj.excludedUserIds = cleanedExcluded;

  const cleanedExtensions: Record<string, number> = {};
  Object.entries(durationExtensionMinutes || {}).forEach(([k, v]) => {
    const idx = inRange(k);
    if (idx === null || !isDurationExtensionPreset(v) || requestOnlyFlags?.[idx]) return;
    cleanedExtensions[String(idx)] = v;
  });
  if (Object.keys(cleanedExtensions).length > 0) obj.durationExtensionMinutes = cleanedExtensions;

  return JSON.stringify(obj);
}

/**
 * Rewrite `optionsJson` while keeping every field the caller doesn't mention —
 * used when a single concern (a branchKey, one option target) changes.
 *
 * Presence of a key decides whether it is overridden, so passing `undefined`
 * explicitly clears a field rather than leaving the existing value in place.
 */
export function patchFieldOptions(
  optionsJson: string | null | undefined,
  patch: Partial<FieldOptionsInput>
): string {
  const parsed = parseFieldOptions(optionsJson);
  const pick = <K extends keyof FieldOptionsInput>(key: K): FieldOptionsInput[K] | undefined =>
    key in patch ? patch[key] : (parsed[key as keyof ParsedFieldOptions] as FieldOptionsInput[K]);

  return stringifyFieldOptions({
    options: pick('options') ?? [],
    requestOnlyFlags: pick('requestOnlyFlags'),
    displayType: pick('displayType'),
    autoAssignedUserIds: pick('autoAssignedUserIds'),
    excludedUserIds: pick('excludedUserIds'),
    optionTargets: pick('optionTargets'),
    branchKey: pick('branchKey'),
    durationExtensionMinutes: pick('durationExtensionMinutes'),
    isDescriptionField: pick('isDescriptionField'),
  });
}

/** Per-option user assignments only. */
export function getAutoAssignedUserIds(
  optionsJson: string | null | undefined
): Record<string, string> {
  return parseFieldOptions(optionsJson).autoAssignedUserIds;
}

/** Per-option user exclusions only. */
export function getExcludedUserIds(
  optionsJson: string | null | undefined
): Record<string, string[]> {
  return parseFieldOptions(optionsJson).excludedUserIds;
}

/**
 * Drop an option and shift every index-keyed map down to match, so per-option
 * assignments, extensions and branch targets stay attached to the right answer.
 */
export function removeOptionAt<T extends FieldOptionsInput>(input: T, index: number): T {
  const rekey = <V,>(map?: Record<string, V>): Record<string, V> => {
    const next: Record<string, V> = {};
    Object.entries(map || {}).forEach(([k, v]) => {
      const i = Number(k);
      if (!Number.isFinite(i) || i === index) return;
      next[i > index ? String(i - 1) : String(i)] = v;
    });
    return next;
  };

  return {
    ...input,
    options: input.options.filter((_, i) => i !== index),
    requestOnlyFlags: (input.requestOnlyFlags || []).filter((_, i) => i !== index),
    autoAssignedUserIds: rekey(input.autoAssignedUserIds),
    excludedUserIds: rekey(input.excludedUserIds),
    durationExtensionMinutes: rekey(input.durationExtensionMinutes),
    optionTargets: rekey(input.optionTargets),
  };
}
